import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { VideoFeatures, VideoFeedback, defaultVideoFeedback } from '@/store/videoFeatures';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Info, Smile, Star, Hash, Clock, Type, MessageCircle, AlertCircle, UserCircle2, Palette, Send, ThumbsUp } from 'lucide-react';
import { ColorEditorPopover } from '@/components/feature-configs/autoplay/ColorEditorPopover';
import { FeedbackTabContent } from '@/components/metrics/feedback/FeedbackTabContent';
import { HeroCard } from '@/components/ui/HeroCard';

interface Props {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
}

const RATING_OPTIONS: Array<{
  id: VideoFeedback['ratingType'];
  title: string;
  desc: string;
  preview: React.ReactNode;
  icon: React.ReactNode;
}> = [
  {
    id: 'emoji',
    get title() { return translateInterfaceText("Emojis"); },
    get desc() { return translateInterfaceText("Ideal para aulas e conteúdos rápidos."); },
    preview: <span className="text-[14px] whitespace-nowrap leading-none">😡 😕 😐 🙂 😄</span>,
    icon: <Smile className="w-4 h-4" />,
  },
  {
    id: 'stars',
    get title() { return translateInterfaceText("Estrelas"); },
    get desc() { return translateInterfaceText("Clássico para satisfação rápida."); },
    preview: <span className="text-[18px] tracking-wide text-[#F5B400]">★ ★ ★ ★ ★</span>,
    icon: <Star className="w-4 h-4" />,
  },
  {
    id: 'nps',
    get title() { return translateInterfaceText("Nota 1 a 5"); },
    get desc() { return translateInterfaceText("Analítico para recomendação."); },
    preview: (
      <span className="text-[12px] font-medium text-[#1A1A1A]">1 2 3 4 5</span>
    ),
    icon: <Hash className="w-4 h-4" />,
  },
];

const TRIGGER_OPTIONS: Array<{ id: VideoFeedback['trigger']; label: string }> = [
  { id: 'video_end', get label() { return translateInterfaceText("No final do vídeo"); } },
  { id: 'pause', get label() { return translateInterfaceText("Ao pausar o vídeo"); } },
  { id: 'custom_time', get label() { return translateInterfaceText("Em tempo personalizado"); } },
];

const IDENT_OPTIONS: Array<{ id: VideoFeedback['identification']; label: string }> = [
  { id: 'none', get label() { return translateInterfaceText("Não solicitar dados"); } },
  { id: 'name', get label() { return translateInterfaceText("Solicitar nome"); } },
  { id: 'name_email', get label() { return translateInterfaceText("Solicitar nome e e-mail"); } },
  { id: 'name_email_whatsapp', get label() { return translateInterfaceText("Solicitar nome, e-mail e WhatsApp"); } },
];

const STYLE_OPTIONS: Array<{ id: VideoFeedback['appearance']['style']; label: string }> = [
  { id: 'glass-dark', get label() { return translateInterfaceText("Vidro escuro"); } },
  { id: 'light', get label() { return translateInterfaceText("Claro premium"); } },
  { id: 'minimal', get label() { return translateInterfaceText("Minimalista"); } },
];

const POSITION_OPTIONS: Array<{ id: VideoFeedback['appearance']['position']; label: string }> = [
  { id: 'center', get label() { return translateInterfaceText("Centro"); } },
  { id: 'bottom-center', get label() { return translateInterfaceText("Inferior central"); } },
  { id: 'bottom-right', get label() { return translateInterfaceText("Inferior direito"); } },
];

const SIZE_OPTIONS: Array<{ id: VideoFeedback['appearance']['size']; label: string }> = [
  { id: 'compact', get label() { return translateInterfaceText("Compacto"); } },
  { id: 'default', get label() { return translateInterfaceText("Padrão"); } },
  { id: 'large', get label() { return translateInterfaceText("Grande"); } },
];

const AFTER_BEHAVIOR: Array<{ id: VideoFeedback['afterSubmit']['behavior']; label: string }> = [
  { id: 'auto-close', get label() { return translateInterfaceText("Fechar automaticamente"); } },
  { id: 'thanks', get label() { return translateInterfaceText("Mostrar mensagem de agradecimento"); } },
];

const AUTO_CLOSE: Array<{ id: VideoFeedback['afterSubmit']['autoCloseSec']; label: string }> = [
  { id: 2, get label() { return translateInterfaceText("2 segundos"); } },
  { id: 3, get label() { return translateInterfaceText("3 segundos"); } },
  { id: 5, get label() { return translateInterfaceText("5 segundos"); } },
  { id: 0, get label() { return translateInterfaceText("Não fechar automaticamente"); } },
];

function Section({ title, subtitle, children }: { title?: string; subtitle?: string; children: React.ReactNode }) {
  useLanguage();
  return (
    <section className="rounded-[16px] border border-[#E5E7EB] bg-white p-5 space-y-4">
      {(title || subtitle) && (
        <header className="space-y-1">
          {title && <h3 className="text-[14px] font-semibold text-[#1A1A1A]">{title}</h3>}
          {subtitle && <p className="text-[12.5px] text-[#6B7280] leading-relaxed">{subtitle}</p>}
        </header>
      )}
      {children}
    </section>
  );
}

function RadioList<T extends string | number>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: Array<{ id: T; label: string }>;
  onChange: (v: T) => void;
}) {
  useLanguage();
  return (
    <div className="space-y-1.5">
      {options.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={String(opt.id)}
            type="button"
            onClick={() => onChange(opt.id)}
            className={`w-full text-left flex items-center gap-3 px-3.5 py-2.5 rounded-[10px] border transition-colors ${
              active
                ? 'border-[#4B7BFF] bg-[#EEF2FF]'
                : 'border-[#E5E7EB] bg-white hover:bg-[#F9FAFB]'
            }`}
          >
            <span
              className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                active ? 'border-[#4B7BFF]' : 'border-[#CBD5E1]'
              }`}
            >
              {active && <span className="w-2 h-2 rounded-full bg-[#4B7BFF]" />}
            </span>
            <span className={`text-[13px] ${active ? 'text-[#1A1A1A] font-medium' : 'text-[#374151]'}`}>
              {opt.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export const VideoFeedbackConfig: React.FC<Props> = ({ features, setFeature }) => {
  useLanguage();
  const fb: VideoFeedback = (features.videoFeedback as VideoFeedback) ?? defaultVideoFeedback;

  const update = (patch: Partial<VideoFeedback>) => {
    setFeature('videoFeedback', { ...fb, ...patch });
  };
  const updateAppearance = (patch: Partial<VideoFeedback['appearance']>) => {
    update({ appearance: { ...fb.appearance, ...patch } });
  };
  const updateComment = (patch: Partial<VideoFeedback['comment']>) => {
    update({ comment: { ...fb.comment, ...patch } });
  };
  const updateAfter = (patch: Partial<VideoFeedback['afterSubmit']>) => {
    update({ afterSubmit: { ...fb.afterSubmit, ...patch } });
  };

  const disabled = !fb.enabled;
  const [activeTab, setActiveTab] = useState<'basic' | 'metrics'>('basic');

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <HeroCard
        icon={<ThumbsUp className="w-full h-full" strokeWidth={2} />}
        title={translateInterfaceText("Feedback do Vídeo")}
        description={translateInterfaceText("Permita que espectadores avaliem o vídeo e acompanhe a percepção real do conteúdo.")}
        isActive={fb.enabled}
        onToggle={(v) => update({ enabled: v })}
      />

      {fb.enabled && (
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'basic' | 'metrics')} className="w-full">
          <TabsList className="w-full bg-[#F7F9FC] rounded-xl p-1">
            <TabsTrigger
              value="basic"
              className="flex-1 h-9 rounded-lg text-[13px] font-medium data-[state=active]:bg-[#4B7BFF] data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:text-[#64748B] data-[state=inactive]:bg-transparent"
            >
              {translateInterfaceText("Personalização")}</TabsTrigger>
            <TabsTrigger
              value="metrics"
              className="flex-1 h-9 rounded-lg text-[13px] font-medium data-[state=active]:bg-[#4B7BFF] data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:text-[#64748B] data-[state=inactive]:bg-transparent"
            >
              {translateInterfaceText("Métricas")}</TabsTrigger>
          </TabsList>

          <TabsContent value="metrics" className="mt-4">
            <FeedbackTabContent videoId={features.videoId ?? null} />
          </TabsContent>

          <TabsContent value="basic" className="mt-4">
            <fieldset disabled={disabled} className={disabled ? 'opacity-50 pointer-events-none space-y-4' : 'space-y-4'}>
        {/* Tipo de avaliação */}
        <Section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {RATING_OPTIONS.map((opt) => {
              const active = fb.ratingType === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => update({ ratingType: opt.id })}
                  className={`text-left rounded-[12px] border p-3.5 transition-colors flex flex-col h-full ${
                    active
                      ? 'border-[#4B7BFF] bg-[#EEF2FF]'
                      : 'border-[#E5E7EB] bg-white hover:bg-[#F9FAFB]'
                  }`}
                >
                  <div className="flex items-center gap-1.5 h-5 mb-2">
                    <span className={`flex items-center gap-1.5 text-[13px] font-semibold ${active ? 'text-[#4B7BFF]' : 'text-[#1A1A1A]'}`}>
                      {opt.icon}
                      {opt.title}
                    </span>
                  </div>
                  <p className="text-[11.5px] text-[#6B7280] leading-relaxed mb-3 min-h-[32px]">{opt.desc}</p>
                  <div className="mt-auto flex justify-center py-2 rounded-[8px] bg-[#F8FAFC]">{opt.preview}</div>
                </button>
              );
            })}
          </div>
        </Section>

        {/* Quando exibir */}
        <section className="rounded-[16px] border border-[#E5E7EB] bg-white p-5 space-y-4">
          <header className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-[10px] bg-[#EEF2FF] flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5 text-[#4B7BFF]" />
            </div>
            <div className="min-w-0">
              <h3 className="text-[14px] font-semibold text-[#1A1A1A] leading-tight">{translateInterfaceText("Quando exibir")}</h3>
              <p className="text-[12.5px] text-[#6B7280] leading-tight mt-0.5">
                {translateInterfaceText("Escolha o momento ideal do feedback.")}</p>
            </div>
          </header>
          <div className="rounded-[10px] bg-[#FEF9E7] border border-[#FCE8A6] px-3 py-2.5 flex items-start gap-2">
            <Info className="w-4 h-4 text-[#B7861B] mt-0.5 shrink-0" />
            <p className="text-[12px] text-[#7A5A0F] leading-relaxed">
              {translateInterfaceText("Para VSLs de venda, recomendamos exibir o feedback apenas no final ou após o pitch para não interromper a conversão.")}</p>
          </div>
          <RadioList value={fb.trigger} options={TRIGGER_OPTIONS} onChange={(v) => update({ trigger: v })} />
          {fb.trigger === 'custom_time' && (() => {
            const total = fb.customTimeSec || 0;
            const mm = Math.floor(total / 60);
            const ss = total % 60;
            const setTime = (m: number, s: number) => {
              const cm = Math.max(0, Math.min(999, m));
              const cs = Math.max(0, Math.min(59, s));
              update({ customTimeSec: cm * 60 + cs });
            };
            return (
              <div className="rounded-[12px] border border-[#E5E7EB] bg-[#FAFBFC] p-3.5 space-y-2.5">
                <div className="flex items-center justify-between">
                  <Label className="text-[12.5px] font-medium text-[#374151]">{translateInterfaceText("Momento exato")}</Label>
                  <span className="text-[11px] text-[#6B7280] tabular-nums">
                    {String(mm).padStart(2,'0')}:{String(ss).padStart(2,'0')}
                  </span>
                </div>
                <div className="flex items-stretch gap-2">
                  <div className="flex-1 flex items-center bg-white border border-[#E5E7EB] rounded-[10px] h-11 px-3 focus-within:border-[#4B7BFF] transition-colors">
                    <input
                      type="number"
                      min={0}
                      max={999}
                      inputMode="numeric"
                      value={mm}
                      onChange={(e) => setTime(parseInt(e.target.value || '0', 10), ss)}
                      onFocus={(e) => e.target.select()}
                      className="w-full bg-transparent outline-none text-[15px] font-semibold text-[#111827] tabular-nums text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <span className="text-[11px] text-[#9CA3AF] ml-1 select-none">{translateInterfaceText("min")}</span>
                  </div>
                  <div className="flex items-center text-[18px] font-semibold text-[#D1D5DB] select-none">:</div>
                  <div className="flex-1 flex items-center bg-white border border-[#E5E7EB] rounded-[10px] h-11 px-3 focus-within:border-[#4B7BFF] transition-colors">
                    <input
                      type="number"
                      min={0}
                      max={59}
                      inputMode="numeric"
                      value={ss}
                      onChange={(e) => setTime(mm, parseInt(e.target.value || '0', 10))}
                      onFocus={(e) => e.target.select()}
                      className="w-full bg-transparent outline-none text-[15px] font-semibold text-[#111827] tabular-nums text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <span className="text-[11px] text-[#9CA3AF] ml-1 select-none">{translateInterfaceText("seg")}</span>
                  </div>
                </div>
              </div>
            );
          })()}

        </section>

        {/* Textos */}
        <section className="rounded-[16px] border border-[#E5E7EB] bg-white p-5 space-y-4">
          <header className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-[10px] bg-[#EEF2FF] flex items-center justify-center shrink-0">
              <Type className="w-5 h-5 text-[#4B7BFF]" />
            </div>
            <div className="min-w-0">
              <h3 className="text-[14px] font-semibold text-[#1A1A1A] leading-tight">{translateInterfaceText("Textos")}</h3>
              <p className="text-[12.5px] text-[#6B7280] leading-tight mt-0.5">
                {translateInterfaceText("Personalize as mensagens do feedback.")}</p>
            </div>
          </header>
          <div className="space-y-3">
            <div>
              <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Título")}</Label>
              <Input
                value={fb.title}
                onChange={(e) => update({ title: e.target.value })}
                placeholder={translateInterfaceText("Como você avalia este conteúdo?")}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Subtexto")}</Label>
              <Input
                value={fb.subtitle}
                onChange={(e) => update({ subtitle: e.target.value })}
                placeholder={translateInterfaceText("Sua resposta ajuda a melhorar a experiência.")}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Texto do botão")}</Label>
              <Input
                value={fb.buttonText}
                onChange={(e) => update({ buttonText: e.target.value })}
                placeholder={translateInterfaceText("Enviar feedback")}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Mensagem de agradecimento")}</Label>
              <Input
                value={fb.thanksMessage}
                onChange={(e) => update({ thanksMessage: e.target.value })}
                placeholder={translateInterfaceText("Obrigado pelo seu feedback!")}
                className="mt-1"
              />
            </div>
          </div>
        </section>


        {/* Comentário */}
        <section className="rounded-[16px] border border-[#E5E7EB] bg-white p-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className={`w-10 h-10 rounded-[10px] flex items-center justify-center shrink-0 transition-colors ${fb.comment.enabled ? 'bg-[#EEF2FF]' : 'bg-[#F3F4F6]'}`}>
                <MessageCircle className={`w-5 h-5 transition-colors ${fb.comment.enabled ? 'text-[#4B7BFF]' : 'text-[#9CA3AF]'}`} />
              </div>
              <div className="min-w-0">
                <h3 className="text-[14px] font-semibold text-[#1A1A1A] leading-tight">{translateInterfaceText("Permitir comentário após avaliação")}</h3>
                <p className="text-[12.5px] text-[#6B7280] leading-tight mt-0.5 whitespace-nowrap">
                  {translateInterfaceText("Permita uma observação após a nota.")}</p>
              </div>
            </div>
            <Switch checked={fb.comment.enabled} onCheckedChange={(v) => updateComment({ enabled: v })} />
          </div>
          {fb.comment.enabled && (
            <div className="space-y-3 pt-1">
              <div>
                <Label className="text-[12px] text-[#374151]">{translateInterfaceText("Placeholder do comentário")}</Label>
                <Textarea
                  value={fb.comment.placeholder}
                  onChange={(e) => updateComment({ placeholder: e.target.value })}
                  rows={2}
                  className="mt-1"
                />
              </div>
              <div className="flex items-center justify-between gap-3 pt-1">
                <div className="flex items-start gap-2.5 min-w-0">
                  <div className={`w-8 h-8 rounded-[8px] flex items-center justify-center shrink-0 transition-colors ${fb.comment.required ? 'bg-[#EEF2FF]' : 'bg-[#F3F4F6]'}`}>
                    <AlertCircle className={`w-4 h-4 transition-colors ${fb.comment.required ? 'text-[#4B7BFF]' : 'text-[#9CA3AF]'}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[13px] font-medium text-[#1A1A1A] leading-tight">{translateInterfaceText("Tornar comentário obrigatório")}</p>
                    <p className="text-[12px] text-[#6B7280] mt-0.5 whitespace-nowrap">
                      {translateInterfaceText("Recomendamos manter opcional.")}</p>
                  </div>
                </div>
                <Switch checked={fb.comment.required} onCheckedChange={(v) => updateComment({ required: v })} />
              </div>
            </div>
          )}
        </section>

        {/* Identificação */}
        <section className="rounded-[16px] border border-[#E5E7EB] bg-white p-5 space-y-4">
          <header className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-[10px] bg-[#EEF2FF] flex items-center justify-center shrink-0">
              <UserCircle2 className="w-5 h-5 text-[#4B7BFF]" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="block text-[14px] font-semibold text-[#1A1A1A] leading-tight">{translateInterfaceText("Identificação")}</h3>
              <p className="block text-[12.5px] text-[#6B7280] leading-tight mt-1 whitespace-nowrap">
                {translateInterfaceText("Vincule a resposta ao lead, quando disponível.")}</p>
            </div>
          </header>
          <RadioList
            value={fb.identification}
            options={IDENT_OPTIONS}
            onChange={(v) => update({ identification: v })}
          />
        </section>

        {/* Aparência */}
        <section className="rounded-[16px] border border-[#E5E7EB] bg-white p-5 space-y-4">
          <header className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-[10px] bg-[#EEF2FF] flex items-center justify-center shrink-0">
              <Palette className="w-5 h-5 text-[#4B7BFF]" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="block text-[14px] font-semibold text-[#1A1A1A] leading-tight">{translateInterfaceText("Aparência")}</h3>
              <p className="block text-[12.5px] text-[#6B7280] leading-tight mt-1 whitespace-nowrap">
                {translateInterfaceText("Personalize estilo, cor e posição do card.")}</p>
            </div>
          </header>
          <div className="space-y-4">
            <div>
              <p className="text-[12px] text-[#374151] mb-2">{translateInterfaceText("Estilo do card")}</p>
              <div className="grid grid-cols-3 gap-2">
                {STYLE_OPTIONS.map((opt) => {
                  const active = fb.appearance.style === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => updateAppearance({ style: opt.id })}
                      className={`px-3 py-2.5 rounded-[10px] border text-[12.5px] transition-colors ${
                        active
                          ? 'border-[#4B7BFF] bg-[#EEF2FF] text-[#4B7BFF] font-medium'
                          : 'border-[#E5E7EB] bg-white text-[#374151] hover:bg-[#F9FAFB]'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="text-[12px] text-[#374151] mb-2">{translateInterfaceText("Cor principal")}</p>
              <div className="flex items-center gap-3 bg-white border border-[#E5E7EB] rounded-[10px] h-11 px-3">
                <ColorEditorPopover
                  colorName={translateInterfaceText("Cor principal")}
                  currentColor={fb.appearance.color}
                  onSave={(c) => updateAppearance({ color: c })}
                >
                  <button
                    type="button"
                    className="w-6 h-6 rounded-full border-2 border-white shadow-[0_1px_3px_rgba(0,0,0,0.18)] cursor-pointer shrink-0"
                    style={{ backgroundColor: fb.appearance.color }}
                  />
                </ColorEditorPopover>
                <input
                  value={fb.appearance.color}
                  onChange={(e) => updateAppearance({ color: e.target.value })}
                  className="flex-1 bg-transparent outline-none text-[13px] text-[#111827]"
                />
              </div>
            </div>

          </div>
        </section>

        {/* Após o envio */}
        <section className="rounded-[16px] border border-[#E5E7EB] bg-white p-5 space-y-4">
          <header className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-[10px] bg-[#EEF2FF] flex items-center justify-center shrink-0">
              <Send className="w-5 h-5 text-[#4B7BFF]" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="block text-[14px] font-semibold text-[#1A1A1A] leading-tight">{translateInterfaceText("Após o envio")}</h3>
              <p className="block text-[12.5px] text-[#6B7280] leading-tight mt-1">
                {translateInterfaceText("Defina o que acontece quando o espectador envia o feedback.")}</p>
            </div>
          </header>
          <RadioList
            value={fb.afterSubmit.behavior}
            options={AFTER_BEHAVIOR}
            onChange={(v) => updateAfter({ behavior: v })}
          />
        </section>
            </fieldset>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default VideoFeedbackConfig;
